import React, { useState } from 'react';
import { Col, Row, Layout, Divider, InputNumber, Button, Modal, Input } from 'antd';
import { useQuery } from '@apollo/client';
import { WarehouseSelectFilter } from '../../../../../components/filters/WarehouseSelectFilter';
import { GET_WAREHOUSES_FOR_CENTRAL, WarehousesResponse } from '../../../exceptions/graphql/queries/getWarehouseForCentral';

/** Line/线路 仅允许：英文字母、数字、下划线、连字符、英文逗号、空格 */
const LINE_INPUT_REGEX = /^[a-zA-Z0-9_\-\s,]*$/;

const isLineInputValid = (value: string): boolean => LINE_INPUT_REGEX.test(value);

// 检查数值的小数位数是否超过2位
const checkDecimalPlaces = (value: number | null | undefined, fieldName: string): boolean => {
    if (value === null || value === undefined) {
        return true;
    }
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    if (decimalIndex === -1) {
        return true;
    }
    const decimalPart = str.substring(decimalIndex + 1);
    if (decimalPart.length > 2) {
        return false;
    }
    return true;
};

const headerStyle: React.CSSProperties = {
    color: '#090909ff',
    lineHeight: '25px',
    backgroundColor: '#fff',
    paddingLeft: '0px',
    paddingRight: '0px',
};

const dividerStyle: React.CSSProperties = {
    margin: '15px 0',
};

export interface DspDeliveryFeeCard {
    warehouseId?: string | null;
    warehouseName?: string;
    sortingCode?: string; // 完整的 sortingCode，如 "LAX-DC"
    line?: string;
    priceUsd?: number | null;
}

export interface DspDeliveryFeeData {
    cards: DspDeliveryFeeCard[];
}

const PriceCard: React.FC<{
    cardIndex: number;
    card: DspDeliveryFeeCard;
    onUpdate: (index: number, card: DspDeliveryFeeCard) => void;
    onDelete: () => void;
    onCopy: () => void;
    disabled?: boolean;
    canDelete?: boolean;
    warehouseData?: WarehousesResponse; // 传递 warehouse 数据
}> = ({ cardIndex, card, onUpdate, onDelete, onCopy, disabled = false, canDelete = true, warehouseData }) => {
    const { Header, Content } = Layout;
    const [localLineError, setLocalLineError] = useState<string | null>(null);
    const lineError =
        localLineError ||
        (card.line && !isLineInputValid(card.line) ? '仅允许字母、数字、英文逗号、空格' : null);

    // 用于临时存储 warehouseId，以便在 onSelectSortingName 回调中一起更新
    const pendingWarehouseIdRef = React.useRef<string | null>(null);

    const handleWarehouseChange = (value: string | undefined) => {
        pendingWarehouseIdRef.current = value || null;
    };

    const handleWarehouseSortingName = (sortingName: string | undefined) => {
        // 根据 warehouseId 查找对应的 sortingCode
        let sortingCode: string | undefined = undefined;
        if (pendingWarehouseIdRef.current && warehouseData?.warehouses) {
            const warehouse = warehouseData.warehouses.find(
                (w: any) => String(w.id) === pendingWarehouseIdRef.current && w.siteType === 2
            );
            if (warehouse) {
                sortingCode = warehouse.sortingCode;
            }
        }
        
        // 同时更新 warehouseId、warehouseName 和 sortingCode
        onUpdate(cardIndex, { 
            ...card, 
            warehouseId: pendingWarehouseIdRef.current, 
            warehouseName: sortingName || '',
            sortingCode: sortingCode
        });
        pendingWarehouseIdRef.current = null;
    };

    // 当 warehouseId 存在但 sortingCode 缺失时，自动填充 sortingCode（用于从加载的数据中恢复）
    React.useEffect(() => {
        if (card.warehouseId && !card.sortingCode && warehouseData?.warehouses) {
            const warehouse = warehouseData.warehouses.find(
                (w: any) => String(w.id) === card.warehouseId && w.siteType === 2
            );
            if (warehouse && warehouse.sortingCode) {
                onUpdate(cardIndex, {
                    ...card,
                    sortingCode: warehouse.sortingCode
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [card.warehouseId, card.sortingCode, warehouseData?.warehouses]);

    const handleLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        if (isLineInputValid(next)) {
            setLocalLineError(null);
            onUpdate(cardIndex, { ...card, line: next });
        } else {
            setLocalLineError('仅允许字母、数字、英文逗号、空格');
        }
    };

    const handlePriceChange = (value: number | null) => {
        if (value !== null && !checkDecimalPlaces(value, '价格')) {
            return;
        }
        onUpdate(cardIndex, { ...card, priceUsd: value });
    };

    return (
        <Layout style={{ backgroundColor: '#fff', padding: 15, border: '1px solid #e8e8e8', borderRadius: 5 }}>
            <Header style={headerStyle}>
                <Row justify="start">
                    <Col span={10}>
                        <span>Warehouse/仓库</span>
                    </Col>
                    <Col span={8}>
                        <span>Line/线路</span>
                    </Col>
                    <Col span={6}>
                        <span>Price/价格</span>
                    </Col>
                </Row>
                <Divider style={dividerStyle} />
            </Header>
            <Content style={{ background: '#fff' }}>
                <Row justify="start" align="top" gutter={[8, 0]}>
                    <Col span={10} >
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <WarehouseSelectFilter
                            label=""
                            value={card.warehouseId || undefined}
                            onChange={handleWarehouseChange}
                            onSelectSortingName={handleWarehouseSortingName}
                            placeholder="选择仓库"
                            className="w-full"
                            disabled={disabled}
                        />
                        </div>
                    </Col>
                    <Col span={8}>
                        <Input
                            value={card.line || ''}
                            onChange={handleLineChange}
                            placeholder="输入线路（仅允许英文逗号或空格分隔）"
                            disabled={disabled}
                            status={lineError ? 'error' : undefined}
                            style={{ width: '100%' }}
                        />
                        {lineError && (
                            <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{lineError}</div>
                        )}
                    </Col>
                    <Col span={6}>
                        <InputNumber
                            value={card.priceUsd ?? undefined}
                            onChange={handlePriceChange}
                            placeholder="输入价格"
                            disabled={disabled}
                            style={{ width: 100 }}
                            min={0.1}
                            precision={2}
                        />
                        <span style={{ marginLeft: 8 }}>美金</span>
                    </Col>
                </Row>
            </Content>
            <Row justify="end" style={{ marginTop: 10 }}>
                <Col>
                    <Button type="default" size="small" onClick={onCopy} disabled={disabled} style={{ marginRight: 8 }}>
                        复制
                    </Button>
                    <Button 
                        type="primary" 
                        danger 
                        size="small" 
                        onClick={onDelete} 
                        disabled={disabled || !canDelete}
                    >
                        删除
                    </Button>
                </Col>
            </Row>
        </Layout>
    );
};

const DspDeliveryFeeTabb: React.FC<{
    tabId: string;
    data?: DspDeliveryFeeData;
    onChange?: (data: DspDeliveryFeeData) => void;
    disabled?: boolean;
}> = ({ tabId, data, onChange, disabled = false }) => {
    // 查询 warehouse 数据，用于根据 warehouseId 获取 sortingCode
    const { data: warehouseData } = useQuery<WarehousesResponse>(GET_WAREHOUSES_FOR_CENTRAL, {
        fetchPolicy: 'cache-first',
    });

    const cards = data?.cards || [{ warehouseId: null, warehouseName: '', sortingCode: undefined, line: '', priceUsd: null }];

    const handleAddCard = () => {
        const newCards = [...cards, { warehouseId: null, warehouseName: '', sortingCode: undefined, line: '', priceUsd: null }];
        onChange?.({ cards: newCards });
    };

    const handleDeleteCard = (index: number) => {
        if (cards.length > 1) {
            Modal.confirm({
                title: '确认删除',
                content: '确定要删除这个价格卡片吗？删除后无法恢复。',
                okText: '确认',
                cancelText: '取消',
                okType: 'danger',
                onOk: () => {
                    const newCards = cards.filter((_, i) => i !== index);
                    onChange?.({ cards: newCards });
                }
            });
        }
    };

    const handleUpdateCard = (index: number, card: DspDeliveryFeeCard) => {
        const newCards = [...cards];
        newCards[index] = card;
        onChange?.({ cards: newCards });
    };

    const handleCopyCard = (index: number) => {
        const cardToCopy = cards[index];
        const copiedCard: DspDeliveryFeeCard = {
            warehouseId: cardToCopy.warehouseId,
            warehouseName: cardToCopy.warehouseName,
            sortingCode: cardToCopy.sortingCode,
            line: cardToCopy.line,
            priceUsd: cardToCopy.priceUsd
        };
        const newCards = [...cards];
        newCards.splice(index + 1, 0, copiedCard);
        onChange?.({ cards: newCards });
    };

    return (
        <div>
            {cards.map((card, index) => (
                <div key={index} style={{ marginBottom: index !== cards.length - 1 ? 15 : 0 }}>
                    <PriceCard
                        cardIndex={index}
                        card={card}
                        onUpdate={handleUpdateCard}
                        onDelete={() => handleDeleteCard(index)}
                        onCopy={() => handleCopyCard(index)}
                        disabled={disabled}
                        canDelete={cards.length > 1}
                        warehouseData={warehouseData}
                    />
                </div>
            ))}
            <Row justify="end" style={{ marginTop: 15 }}>
                <Col>
                    <Button type="primary" size="middle" onClick={handleAddCard} disabled={disabled}>
                        新增
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

export default DspDeliveryFeeTabb;
